/**
 * Мастер: добавить поле черновика по профилю правил (фаза 6).
 * PUT шаблон с новым полем → POST apply-rule-profile.
 */

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { cardTemplatesApi } from "@/services/card-api.service";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { CardFieldWidgetTypeItem } from "@/types/card-template";
import type {
  CardAppliesTo,
  CardFieldType,
  CardTemplateFieldCreate,
  CardTemplateSectionCreate,
} from "@/types/card-template";
import { FIELD_TYPE_LABELS } from "@/types/card-template";
import { DEFAULT_POSITIVE_NUMBER_PROFILE_CODE } from "@/types/rule-profile";

function defaultSectionCode(sections: CardTemplateSectionCreate[]): string {
  const obj = sections.filter((s) => s.applies_to !== "geometry");
  const ordered = [...obj].sort(
    (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)
  );
  return ordered[0]?.code ?? sections[0]?.code ?? "";
}

interface FieldFromProfileWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sections: CardTemplateSectionCreate[];
  fieldWidgetTypes: CardFieldWidgetTypeItem[];
  onApply: (field: CardTemplateFieldCreate, profileId: number) => Promise<void>;
  isBusy?: boolean;
}

export function FieldFromProfileWizard({
  open,
  onOpenChange,
  sections,
  fieldWidgetTypes,
  onApply,
  isBusy,
}: FieldFromProfileWizardProps) {
  const { toast } = useToast();
  const { data: profilesData } = useQuery({
    queryKey: ["card-templates", "rule-profiles", "list"],
    queryFn: () => cardTemplatesApi.listRuleProfiles({ is_active: true, limit: 200 }),
    enabled: open,
    staleTime: 60_000,
  });

  const profiles = profilesData?.items ?? [];

  const [profileId, setProfileId] = useState("");
  const [fieldKey, setFieldKey] = useState("new_numeric_field");
  const [label, setLabel] = useState("Число");
  const [qualId, setQualId] = useState("1");
  const [fieldType, setFieldType] = useState<CardFieldType>("number");
  const [appliesTo, setAppliesTo] = useState<CardAppliesTo>("object");
  const [sectionCode, setSectionCode] = useState("");

  useEffect(() => {
    if (!open) return;
    setSectionCode(defaultSectionCode(sections));
  }, [open, sections]);

  useEffect(() => {
    if (!open || profiles.length === 0) return;
    const preferred =
      profiles.find((p) => p.code === DEFAULT_POSITIVE_NUMBER_PROFILE_CODE) ??
      profiles.find((p) => (p.items?.length ?? 0) > 0) ??
      profiles[0];
    if (preferred) setProfileId(String(preferred.id));
  }, [open, profiles]);

  const klsIdForType = useMemo(() => {
    const meta = fieldWidgetTypes.find((w) => w.kls_code === fieldType);
    return meta?.kls_id;
  }, [fieldWidgetTypes, fieldType]);

  const handleSubmit = async () => {
    const pk = fieldKey.trim();
    const lb = label.trim();
    const q = Number(qualId);
    const pid = Number(profileId);
    if (!pk || !lb || !Number.isFinite(q) || q <= 0) {
      toast({
        title: "Заполните поля",
        description: "Нужны ключ, название и положительный qual_id.",
        variant: "destructive",
      });
      return;
    }
    if (!Number.isFinite(pid) || pid <= 0) {
      toast({
        title: "Выберите профиль",
        description: "Нет доступных профилей или не выбран профиль.",
        variant: "destructive",
      });
      return;
    }

    const newField: CardTemplateFieldCreate = {
      field_key: pk,
      label: lb,
      field_type: fieldType,
      field_type_kls_id: klsIdForType,
      qual_id: q,
      applies_to: appliesTo,
      section_code: sectionCode.trim() || null,
      is_required: false,
      is_readonly: false,
      is_multiple: false,
      is_active: true,
      display_order: 1000,
      validation_rules: {},
      ui_config: {},
      visibility_rules: {},
      permissions_config: {},
      inherit_from_object: false,
    };

    try {
      await onApply(newField, pid);
      onOpenChange(false);
    } catch (e) {
      toast({
        title: "Ошибка",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent nestedAboveSheet className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Поле из профиля правил</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <Label className="text-xs">Профиль</Label>
            <Select value={profileId} onValueChange={setProfileId}>
              <SelectTrigger className="h-9 mt-1">
                <SelectValue placeholder="Выберите профиль" />
              </SelectTrigger>
              <SelectContent>
                {profiles.map((p) => (
                  <SelectItem key={p.id} value={String(p.id)}>
                    {p.name}{" "}
                    <span className="text-muted-foreground font-mono text-[11px]">
                      ({p.code})
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {profiles.length === 0 && (
              <p className="text-[11px] text-muted-foreground mt-1">
                Профили не найдены. Создайте профиль (например, код «{DEFAULT_POSITIVE_NUMBER_PROFILE_CODE}»)
                через API.
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">field_key</Label>
              <Input
                className="h-9 mt-1 font-mono text-xs"
                value={fieldKey}
                onChange={(e) => setFieldKey(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs">Название</Label>
              <Input
                className="h-9 mt-1"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">qual_id</Label>
              <Input
                className="h-9 mt-1"
                type="number"
                value={qualId}
                onChange={(e) => setQualId(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs">Тип</Label>
              <Select
                value={fieldType}
                onValueChange={(v) => setFieldType(v as CardFieldType)}
              >
                <SelectTrigger className="h-9 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(FIELD_TYPE_LABELS) as CardFieldType[]).map((ft) => (
                    <SelectItem key={ft} value={ft}>
                      {FIELD_TYPE_LABELS[ft]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Уровень</Label>
              <Select
                value={appliesTo}
                onValueChange={(v) => setAppliesTo(v as CardAppliesTo)}
              >
                <SelectTrigger className="h-9 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="object">Объект</SelectItem>
                  <SelectItem value="geometry">Геометрия</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Секция (code)</Label>
              <Input
                className="h-9 mt-1 font-mono text-xs"
                value={sectionCode}
                onChange={(e) => setSectionCode(e.target.value)}
                placeholder="пусто = авто"
              />
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">
            После сохранения к полю будет применён выбранный профиль (в т.ч. при TEMPLATE_RULES_DUAL_WRITE).
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} type="button">
            Отмена
          </Button>
          <Button onClick={handleSubmit} disabled={isBusy || profiles.length === 0}>
            {isBusy ? "Сохранение…" : "Создать и применить профиль"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
